import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import CirclePause from '@gravity-ui/icons/CirclePause';
import Ellipsis from '@gravity-ui/icons/Ellipsis';
import TrashBin from '@gravity-ui/icons/TrashBin';
import { Button, DropdownMenu, Icon, Label, Link, Loader, TextArea, useToaster } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import clx from 'classnames';
import {
    ApiError,
    deleteApp,
    forceStopApp,
    requestCancelInstallableAppModeration,
    requestDeploy,
    requestInstallableAppReview,
    stopApp,
    updateInstallableAppDraft,
} from 'client/api';
import { ErrorPayload } from 'client/api/error';
import { IconMedium } from 'client/blocks/AppIcons/AppIcons';
import { Link as ExternalLink } from 'client/blocks/Link/Link';
import { ModalWithConfirmation } from 'client/blocks/ModalWithConfirmation/ModalWithConfirmation';
import { OperationStatus } from 'client/blocks/statuses/OperationStatus/OperationStatus';
import { CurrentAliceAppContext } from 'client/context/aliceAppContext';
import { FormChangeHandler, FormErrors } from 'client/hocs/types';
import { useIsDraft } from 'client/hooks/useIsDraft';
import { wrapInText } from 'client/lib/i18n-utils';
import { routes } from 'client/lib/routes';
import { lengthConfig } from 'client/lib/validation';
import { DraftStatus } from 'client/model/draft';
import { Channel, InstallableApp } from 'client/model/skill';
import { InstallableAppFormSettings } from 'client/types';
import { ApiValidationError, AppError } from 'client/types/errors';
import { getCanEditApp, getCanEditAsAdmin } from 'client/utils';
import {
    convertToErrors,
    createUpdateError,
    hasFieldErrors,
    mapApiErrorsToFormErrors,
    translateAppStoreError,
} from 'client/utils/errorMessage';
import { mapValuesToInstallableAppSettings } from 'client/utils/settings/installable-app';
import * as appFormKeyset from 'i18n/AppForm';
import * as appsIndexKeyset from 'i18n/AppsIndex';
import * as keyset from 'i18n/DraftControlPanel';
import * as errorsKeyset from 'i18n/SettingsErrors';
import * as settingsLabels from 'i18n/SettingsLabels';

import styles from './ControlPanel.module.scss';

type TModal =
    | 'requestReview'
    | 'cancelReview'
    | 'changeToDraft'
    | 'deleteApp'
    | 'stopApp'
    | 'forceStopApp'
    | 'esiaError'
    | 'porgIsNotVerified'
    | 'saveWarnings'
    | 'submitWarnings';

const INSTALLABLE_APP_CONTROL_PANEL_TOAST = 'INSTALLABLE_APP_CONTROL_PANEL_TOAST';

const t = i18n(keyset);
const tError = i18n(errorsKeyset);
const tSettings = i18n(settingsLabels);
const tAppForm = i18n(appFormKeyset);
const tAppsIndex = i18n(appsIndexKeyset);

interface ControlPanelProps {
    isSubmitting?: boolean;
    setErrors: (errors: FormErrors<InstallableAppFormSettings & { network: string }>, cb?: () => void) => void;
    isNonValidationError: boolean;
    settingValues: InstallableAppFormSettings;
    onSettingValuesChange: FormChangeHandler<InstallableAppFormSettings>;
    settingFormErrors: FormErrors<InstallableAppFormSettings & { esia?: string }>;
    appName: string;
    logoUrl: string;
    values: InstallableAppFormSettings;
    saveWarnings: ApiValidationError[];
    clearSaveWarnings: () => void;
}

const convertTypedErrorToMessage = (key: string, value: string) => {
    const typedKey = key as keyof FormErrors<InstallableAppFormSettings & { network?: string; esia?: string }>;
    if (typedKey === 'activationPhrases') {
        return `${tSettings('activation-phrases')}: ${value}`;
    }
    if (typedKey === 'screenshots') {
        return `${tSettings('screenshots')}: ${value}`;
    }
    if (typedKey === 'apkInfosWithTargets') {
        return `${tAppForm('apk-title')}: ${value}`;
    }
    if (typedKey === 'eulaAgreement') {
        return `${tAppForm('eula-title')}: ${value}`;
    }
    if (typedKey === 'privacyAgreement') {
        return `${tAppForm('private-policy-title')}: ${value}`;
    }
    if (
        typedKey === 'network' &&
        value === 'Name/ActivationPhrases validation error: &apos;Содержит общие фразы&apos;'
    ) {
        return `${tSettings('activation-phrases')}: ${value}`;
    }
    if (typedKey === 'network') {
        return `${tError('network')}: ${value}`;
    }
    if (typedKey === 'esia') {
        return `${tError('need-esia-profile-title')}: ${value}`;
    }
    return String(value);
};

const getModalType = (error: ErrorPayload): TModal | undefined => {
    if (error?.typedErrors?.some((error) => error.type === AppError.NO_ESIA_PROFILE)) return 'esiaError';
    if (error?.typedErrors?.some((error) => error.type === AppError.PORGANISATION_IS_NOT_VERIFIED))
        return 'porgIsNotVerified';
};

export const InstallableAppControlPanel: React.FC<ControlPanelProps> = ({
    appName,
    isSubmitting,
    setErrors,
    isNonValidationError,
    settingValues,
    onSettingValuesChange,
    settingFormErrors,
    logoUrl,
    values,
    saveWarnings,
    clearSaveWarnings,
}) => {
    const [forceStopComment, setForceStopComment] = useState<string>();
    const [openModalType, setOpenModalType] = useState<TModal | undefined>();
    const [isLoading, setIsLoading] = useState(false);
    const [submitWarnings, setSubmitWarnings] = useState<ApiValidationError[]>([]);

    const { add: addToast } = useToaster();

    const aliceAppContext = useContext(CurrentAliceAppContext)!;
    const aliceApp = aliceAppContext.aliceApp as InstallableApp;
    const { refreshAliceApp, operations } = aliceAppContext;

    const { isDraft, setIsDraft } = useIsDraft(aliceApp.channel, aliceApp.id);
    const navigate = useNavigate();
    const canEditAsAdmin = getCanEditAsAdmin();
    const disabled = !getCanEditApp(aliceApp);

    useEffect(() => {
        setForceStopComment(undefined);
    }, [aliceApp.id]);

    useEffect(() => {
        if (saveWarnings.length > 0) {
            setOpenModalType('saveWarnings');
        }
    }, [saveWarnings]);

    const formErrors = convertToErrors(settingFormErrors, convertTypedErrorToMessage);
    const hasValidationErrors = hasFieldErrors(settingFormErrors);

    if (isNonValidationError) {
        formErrors.push(tError('unknown-error'));
    }

    const wrapActionCall = useCallback(
        (callback: (appId: string, appChannel: Channel, comment?: string) => Promise<void | InstallableApp>) => {
            return async () => {
                setIsLoading(true);
                try {
                    await callback(aliceApp.id, aliceApp.channel);
                    refreshAliceApp();
                    setOpenModalType(undefined);
                } catch (error) {
                    if (error instanceof ApiError) {
                        setErrors(createUpdateError(error, aliceApp.channel));
                        setOpenModalType(getModalType(error?.payload));
                        addToast &&
                            addToast({
                                title: error.message,
                                theme: 'danger',
                                name: INSTALLABLE_APP_CONTROL_PANEL_TOAST,
                            });
                    } else {
                        let errorMessage = tError('network');
                        if (
                            !!error &&
                            typeof error === 'object' &&
                            'message' in error &&
                            typeof error.message === 'string'
                        ) {
                            errorMessage = error.message;
                        }
                        setErrors(mapApiErrorsToFormErrors(errorMessage));
                        setOpenModalType(undefined);
                        addToast &&
                            addToast({
                                title: errorMessage,
                                theme: 'danger',
                                name: INSTALLABLE_APP_CONTROL_PANEL_TOAST,
                            });
                    }
                }
                setIsLoading(false);
            };
        },
        [aliceApp.id, aliceApp.channel, refreshAliceApp, setErrors, addToast],
    );

    const cancelReview = wrapActionCall(requestCancelInstallableAppModeration);
    const requestDeployAction = wrapActionCall(requestDeploy);
    const stopInstallableApp = wrapActionCall(stopApp);

    const submitForReview = useCallback(async () => {
        setIsLoading(true);
        try {
            await requestInstallableAppReview(aliceApp.id, aliceApp.channel);
            refreshAliceApp();
            setSubmitWarnings([]);
            setOpenModalType(undefined);
        } catch (error) {
            if (error instanceof ApiError) {
                setErrors(createUpdateError(error, aliceApp.channel));
                setOpenModalType(getModalType(error?.payload));
                addToast &&
                    addToast({
                        title: error.message,
                        theme: 'danger',
                        name: INSTALLABLE_APP_CONTROL_PANEL_TOAST,
                    });
            } else {
                let errorMessage = tError('network');
                if (!!error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
                    errorMessage = error.message;
                }
                setErrors(mapApiErrorsToFormErrors(errorMessage));
                setOpenModalType(undefined);
                addToast &&
                    addToast({
                        title: errorMessage,
                        theme: 'danger',
                        name: INSTALLABLE_APP_CONTROL_PANEL_TOAST,
                    });
            }
        }
        setIsLoading(false);
    }, [aliceApp.id, aliceApp.channel, refreshAliceApp, setErrors, addToast]);

    const requestReview = useCallback(async () => {
        setIsLoading(true);
        try {
            const settings = mapValuesToInstallableAppSettings(values, aliceApp.channel);
            const result = await updateInstallableAppDraft(aliceApp.id, settings);
            const draftWarnings = result?.draft?.warnings ?? [];
            if (draftWarnings.length > 0) {
                setSubmitWarnings(draftWarnings);
                setOpenModalType('submitWarnings');
                setIsLoading(false);
                return;
            }
            await requestInstallableAppReview(aliceApp.id, aliceApp.channel);
            refreshAliceApp();
            setSubmitWarnings([]);
            setOpenModalType(undefined);
        } catch (error) {
            if (error instanceof ApiError) {
                setErrors(createUpdateError(error, aliceApp.channel));
                setOpenModalType(getModalType(error?.payload));
                addToast &&
                    addToast({
                        title: error.message,
                        theme: 'danger',
                        name: INSTALLABLE_APP_CONTROL_PANEL_TOAST,
                    });
            } else {
                let errorMessage = tError('network');
                if (!!error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
                    errorMessage = error.message;
                }
                setErrors(mapApiErrorsToFormErrors(errorMessage));
                setOpenModalType(undefined);
                addToast &&
                    addToast({
                        title: errorMessage,
                        theme: 'danger',
                        name: INSTALLABLE_APP_CONTROL_PANEL_TOAST,
                    });
            }
        }
        setIsLoading(false);
    }, [aliceApp.id, aliceApp.channel, values, refreshAliceApp, setErrors, addToast]);

    const deleteInstallableApp = useCallback(async () => {
        setIsLoading(true);
        try {
            await deleteApp(aliceApp.id, aliceApp.channel);
            setTimeout(async () => {
                navigate(routes.app.installableApps(aliceApp.channel));
                refreshAliceApp();
            }, 3000);
        } catch (error) {
            let errorMessage = tError('network');
            if (!!error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
                errorMessage = error.message;
            }
            setErrors(mapApiErrorsToFormErrors(errorMessage));
        }
        setIsLoading(false);
        setOpenModalType(undefined);
    }, [aliceApp.id, aliceApp.channel, refreshAliceApp, setErrors, navigate]);

    const forceStopInstallableApp = useCallback(
        async (comment?: string) => {
            if (!comment) return;
            setIsLoading(true);
            try {
                await forceStopApp(aliceApp.id, aliceApp.channel, { comment });
                refreshAliceApp();
            } catch (error) {
                let errorMessage = tError('network');
                if (!!error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
                    errorMessage = error.message;
                }
                setErrors(mapApiErrorsToFormErrors(errorMessage));
            }
            setIsLoading(false);
            setOpenModalType(undefined);
        },
        [aliceApp.id, aliceApp.channel, refreshAliceApp, setErrors],
    );

    if (!operations.length) {
        return (
            <div className={styles.buttonsContainer}>
                <Loader />
            </div>
        );
    }

    let disableModerationButton = !aliceApp.draft.isAllowedForDeploy || (aliceApp.isBanned && !canEditAsAdmin);
    const disableStopButton = false;

    let hideSaveButton = true;
    let hideModerationButton = true;
    let hideStopButton = false;
    let hideDeployButton = true;
    let hideChangeButton = false;

    if (isDraft) {
        disableModerationButton = !aliceApp.draft.isAllowedForDeploy || (aliceApp.isBanned && !canEditAsAdmin);

        hideSaveButton = aliceApp.draft.status !== DraftStatus['InDevelopment'];
        hideModerationButton = !isDraft || aliceApp.draft.status !== DraftStatus['InDevelopment'];
        hideStopButton = true;
        hideDeployButton = aliceApp.draft.status !== DraftStatus['ReviewApproved'];
        hideChangeButton = aliceApp.draft.status === DraftStatus['InDevelopment'];
    }

    if (disabled) {
        disableModerationButton = true;
        hideSaveButton = true;
        hideModerationButton = true;
        hideStopButton = true;
        hideDeployButton = true;
        hideChangeButton = true;
    }

    let modal = null;

    if (openModalType === 'requestReview') {
        modal = (
            <ModalWithConfirmation
                onClose={() => setOpenModalType(undefined)}
                confirmButtonLabel={t('send-to-moderation')}
                title={t('ready-for-moderation')}
                onConfirm={requestReview}
                loading={isLoading}
            >
                <p className={styles.grayNote}>{t('moderator-comment-placeholder')}</p>
                <TextArea
                    className={styles.moderatorCommentTextfield}
                    error={settingFormErrors.noteForModerator}
                    note={
                        <span className={styles.grayNote}>
                            {lengthConfig.noteForModerator - (settingValues.noteForModerator?.length || 0)}
                        </span>
                    }
                    onChange={(e) => onSettingValuesChange('noteForModerator')(e.target.value)}
                    placeholder={t('moderator-comment-placeholder')}
                    rows={5}
                    size="xl"
                    value={settingValues.noteForModerator}
                />
            </ModalWithConfirmation>
        );
    }
    if (openModalType === 'cancelReview') {
        modal = (
            <ModalWithConfirmation
                onClose={() => setOpenModalType(undefined)}
                confirmButtonLabel={t('change')}
                title={t('changing-data-will-stop-moderation')}
                text={t('if-you-want-to-change-the-data')}
                onConfirm={cancelReview}
                loading={isLoading}
            />
        );
    }
    if (openModalType === 'changeToDraft') {
        modal = (
            <ModalWithConfirmation
                onClose={() => setOpenModalType(undefined)}
                confirmButtonLabel={t('go-to-draft')}
                title={t('change-in-copy')}
                text={t('you-can-not-change-published-version')}
                onConfirm={() => setIsDraft(true, aliceApp.channel)}
                loading={isLoading}
            />
        );
    }
    if (openModalType === 'deleteApp') {
        modal = (
            <ModalWithConfirmation
                onClose={() => setOpenModalType(undefined)}
                confirmButtonLabel={t('delete')}
                text={t('delete-text')}
                warning={t('delete-warning')}
                title={t('delete-title')}
                onConfirm={deleteInstallableApp}
                loading={isLoading}
            />
        );
    }
    if (openModalType === 'stopApp') {
        modal = (
            <ModalWithConfirmation
                onClose={() => setOpenModalType(undefined)}
                confirmButtonLabel={t('stop')}
                text={t('stop-text')}
                title={t('stop-title')}
                onConfirm={stopInstallableApp}
                loading={isLoading}
            />
        );
    }
    if (openModalType === 'forceStopApp') {
        modal = (
            <ModalWithConfirmation
                onClose={() => setOpenModalType(undefined)}
                confirmButtonLabel={t('stop')}
                title={t('stop-title')}
                text={t('stop-text')}
                onConfirm={() => forceStopInstallableApp(forceStopComment)}
                loading={isLoading}
                confirmDisabled={!forceStopComment}
            >
                <TextArea
                    className={styles.moderatorCommentTextfield}
                    error={settingFormErrors.noteForModerator}
                    note={
                        <span className={styles.grayNote}>
                            {lengthConfig.noteForModerator - (settingValues.noteForModerator?.length || 0)}
                        </span>
                    }
                    onChange={(e) => setForceStopComment(e.target.value)}
                    placeholder={t('force-stop-not-empty-comment')}
                    rows={5}
                    size="xl"
                    value={forceStopComment}
                    errorMessage={!forceStopComment && t('force-stop-not-empty-comment')}
                />
            </ModalWithConfirmation>
        );
    }
    if (openModalType === 'esiaError') {
        const esiaLink = `https://social.yandex.ru/broker2/start?provider=esia&display=popup&consumer=consumer&bind=1&retpath=${location.origin + routes.app.installableApps(Channel.TvApp)}`;

        modal = (
            <ModalWithConfirmation
                onClose={() => setOpenModalType(undefined)}
                confirmButtonLabel={tError('link')}
                text={
                    <>
                        {tError('need-esia-profile-text')}{' '}
                        <Link href={esiaLink} target="_blank">
                            {tError('go-to-esia')}
                        </Link>
                    </>
                }
                title={tError('need-esia-profile-title')}
                onConfirm={() => window.open(esiaLink, '_blank')}
                displayClose={false}
            />
        );
    }

    const renderWarningsList = (warnings: ApiValidationError[]) => (
        <ul className={styles.warningsList}>
            {warnings.map((warning, idx) => (
                <li key={`${warning.type}-${warning.key ?? idx}-${idx}`}>
                    {translateAppStoreError(warning.type, warning.arg)}
                </li>
            ))}
        </ul>
    );

    if (openModalType === 'saveWarnings') {
        const closeSaveWarningsModal = () => {
            setOpenModalType(undefined);
            clearSaveWarnings();
        };
        modal = (
            <ModalWithConfirmation
                onClose={closeSaveWarningsModal}
                confirmButtonLabel={t('warnings-ok')}
                title={t('warnings-title')}
                text={
                    <>
                        <p>{t('warnings-save-text')}</p>
                        {renderWarningsList(saveWarnings)}
                    </>
                }
                onConfirm={closeSaveWarningsModal}
                loading={isLoading}
                displayCancelButton={false}
            />
        );
    }

    if (openModalType === 'submitWarnings') {
        const closeSubmitWarningsModal = () => {
            setOpenModalType(undefined);
            setSubmitWarnings([]);
        };
        modal = (
            <ModalWithConfirmation
                onClose={closeSubmitWarningsModal}
                confirmButtonLabel={t('warnings-send-anyway')}
                cancelButtonLabel={t('warnings-back-to-edit')}
                title={t('warnings-title')}
                text={
                    <>
                        <p>{t('warnings-moderation-text')}</p>
                        {renderWarningsList(submitWarnings)}
                    </>
                }
                onConfirm={submitForReview}
                loading={isLoading}
            />
        );
    }

    if (openModalType === 'porgIsNotVerified') {
        const porgVerifyDocsLink = routes.documentation.aliceApps.porgVerify;

        modal = (
            <ModalWithConfirmation
                onClose={() => setOpenModalType(undefined)}
                confirmButtonLabel={tAppsIndex('docs')}
                text={wrapInText((s) => (
                    <ExternalLink theme="info" external target="_blank" to={porgVerifyDocsLink}>
                        {s}
                    </ExternalLink>
                ))(tError('need-link-porg-to-gosuslugi'))}
                title={tError('need-esia-profile-title')}
                onConfirm={() => window.open(porgVerifyDocsLink, '_blank')}
                displayClose={false}
            />
        );
    }

    return (
        <>
            <div className={styles.container}>
                <div className={styles.appTitle}>
                    <IconMedium imageUrl={logoUrl} />
                    <h1 className={styles.appTitle__header}>{appName}</h1>
                    <div className={styles.appTitle__mobileStatusIcon}>
                        <OperationStatus isDraft={isDraft} />
                    </div>
                </div>
                <div className={styles.buttonsContainer}>
                    <Button
                        className={clx(hideSaveButton && styles.button_hidden)}
                        disabled={hasValidationErrors}
                        loading={isSubmitting || isLoading}
                        type="submit"
                    >
                        {t('save')}
                    </Button>
                    <Button
                        className={clx(hideModerationButton && styles.button_hidden)}
                        view={aliceApp.draft.isAllowedForDeploy ? 'action' : 'normal'}
                        disabled={disableModerationButton}
                        onClick={() => setOpenModalType('requestReview')}
                        loading={isLoading}
                    >
                        {t('send-to-moderation')}
                    </Button>
                    <Button
                        className={clx(hideStopButton && styles.button_hidden)}
                        disabled={disableStopButton}
                        onClick={() => setOpenModalType(canEditAsAdmin ? 'forceStopApp' : 'stopApp')}
                        loading={isLoading}
                    >
                        <Icon data={CirclePause} />
                        {t('stop')}
                    </Button>
                    <Button
                        className={clx(hideDeployButton && styles.button_hidden)}
                        view="action"
                        onClick={requestDeployAction}
                        loading={isLoading}
                    >
                        {t('action-deploy')}
                    </Button>
                    <Button
                        className={clx(hideChangeButton && styles.button_hidden)}
                        onClick={() => setOpenModalType(isDraft ? 'cancelReview' : 'changeToDraft')}
                        loading={isLoading}
                    >
                        {t('change')}
                    </Button>
                    {!disabled && (
                        <DropdownMenu
                            renderSwitcher={(props) => (
                                <Button {...props}>
                                    <Icon size={16} data={Ellipsis} />
                                </Button>
                            )}
                            items={[
                                {
                                    action: () => setOpenModalType('deleteApp'),
                                    iconStart: <Icon size={16} data={TrashBin} />,
                                    text: t('delete'),
                                    theme: 'danger',
                                },
                            ]}
                        />
                    )}
                </div>
            </div>
            <div className={clx(styles.errorsBlock, formErrors.length === 0 && styles.errorsBlock_hidden)}>
                {formErrors.map((errorMessage) => (
                    <Label key={errorMessage} className={styles.errorLabel} theme="danger">
                        {errorMessage}
                    </Label>
                ))}
            </div>
            {modal}
        </>
    );
};
